import React from 'react'
import {
  Container, Col, Nav, Row, NavItem,
} from 'reactstrap'
import logoDark from 'assets/img/logos/logo-word-dark.png'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { SessionProvider } from 'components/auth/Session'
import { isViewTypes } from 'helpers/routing'
import { NavLinkProps, NavLink } from 'react-router-dom'
import { LoadingProvider } from 'components/common/Loading'
import OnboardingBar from 'components/common/OnboardingBar'
import { LayoutTitle } from '../teleporters/LayoutTitle'

const StyledNav = styled(Nav)`
  text-align: left;

  @media (max-width: 992px) {
    margin-top: 4px !important;
  }
`

const StyledSideNavCol = styled(Col)`
  padding: 45px 25px;

  @media (max-width: 992px) {
    img {
      display: none;
    }
  }
`

const StyledBaseCol = styled(Col)`
  box-shadow: 0 0 0 1px rgba(0,0,0,0.0), 0 4px 24px 2px rgba(0,0,0,0.05);
  border-radius: 14px;
  background-color: white;
  padding: 30px 50px;
`

const StyledNavItem = styled(NavItem)`
  height: 55px;
  white-space: nowrap;

  @media (max-width: 992px) {
    text-align: center;
    padding-left: 25px;

    a {
      font-size: 22px;
    }

    span {
      display: none;
    }
  }

  @media (max-width: 1200px) {
    padding-left: 15px;
  }
`

const InternalNavLink: React.FC<NavLinkProps & { active?: boolean }> = ({ children, active, ...props }) => (
  <NavLink activeClassName="active" className={`nav-link ${active ? 'active' : ''}`} {...props}>
    {children}
  </NavLink>
)

const StyledNavLink = styled(InternalNavLink)`
  font-family: Poppins;
  font-weight: 500;
  font-size: 16px;
  color: #293579 !important;
  letter-spacing: 0;
  opacity: ${(props) => (props.active ? 1 : 0.35)};
  svg {
    margin-right: 20px;
  }

  &:hover {
    opacity: .8;
  }
`

export const Layout: React.FC = ({ children }) => {
  const activeDash = isViewTypes(window.location, 'dashboard[a-zA-Z0-9]*')
    || isViewTypes(window.location, '^/(\\?.*)?$')
  const activeTracing = isViewTypes(window.location, 'tracing[a-zA-Z0-9]*')
    || isViewTypes(window.location, 'case[a-zA-Z0-9]*')
  const activeSpaces = isViewTypes(window.location, 'spaces[a-zA-Z0-9]*')
    || isViewTypes(window.location, 'space[a-zA-Z0-9]*')

  let onboardingNode = null
  if (activeSpaces) {
    onboardingNode = (
      <OnboardingBar
        title="Quick Start"
        items={[
          { index: '1', label: 'Download & print out QR code' },
          { index: '2', label: 'Place at the entry to the space' },
          { index: '3', label: 'People scan to check in' },
        ]}
      />
    )
  }

  return (
    <SessionProvider>
      <Container fluid>
        <Row className="min-vh-100">
          <StyledSideNavCol>
            <img src={logoDark} alt="logo" width="130px" />

            <StyledNav vertical className="mt-5">
              <StyledNavItem>
                <StyledNavLink active={activeDash} to="/">
                  <FontAwesomeIcon icon="chart-bar" />
                  <span>Dashboard</span>
                </StyledNavLink>
              </StyledNavItem>
              <StyledNavItem>
                <StyledNavLink active={activeTracing} to="/tracing">
                  <FontAwesomeIcon icon="shield-alt" />
                  <span>Tracing</span>
                </StyledNavLink>
              </StyledNavItem>
              <StyledNavItem>
                <StyledNavLink active={activeSpaces} to="/spaces">
                  <FontAwesomeIcon icon="map-marker-alt" />
                  <span>Spaces</span>
                </StyledNavLink>
              </StyledNavItem>
              <StyledNavItem>
                <StyledNavLink to="/auth/logout">
                  <FontAwesomeIcon icon="sign-out-alt" />
                  <span>Logout</span>
                </StyledNavLink>
              </StyledNavItem>
            </StyledNav>
          </StyledSideNavCol>

          <StyledBaseCol xs="10">
            {onboardingNode}
            <LoadingProvider>
              <LayoutTitle.Target />
              {children}
            </LoadingProvider>
          </StyledBaseCol>
        </Row>
      </Container>
    </SessionProvider>
  )
}

export default Layout
